import { useState, useEffect, useMemo } from 'react';
import { TvaToggle, useTva, tvaPct, tvaAmount } from './TvaToggle';
import axios from 'axios';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import QuickAddEntrepriseButton from './QuickAddEntrepriseButton';
import {
  Search, Building2, Truck, Plus, Trash2, FileText, Video,
  Film, Camera, Sparkles, Mic, Music, Wand2, Users, PlayCircle, ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';

const fmtXAF = (n) => new Intl.NumberFormat('fr-FR').format(Math.round(n || 0));

const OBJECTIFS = [
  'Vidéo institutionnelle',
  'Publicité produit',
  'Réseaux sociaux',
  'Événementiel',
  'Formation / e-learning',
  'Documentaire',
  'Autre',
];

const TYPES_VIDEO = [
  'Vidéo courte',
  'Vidéo moyenne',
  'Vidéo longue',
];

const DUREES = [
  '15 - 30 secondes',
  '30 - 60 secondes',
  '1 - 3 minutes',
  '3 - 5 minutes',
  '5 - 15 minutes',
  '> 15 minutes',
];

const LANGUES = ['Français', 'Sango', 'Anglais', 'Bilingue (FR/EN)'];

const TRANSPORT_MODES = [
  'Transport routier',
  'Transport aérien',
  'Livraison locale',
  'Retrait sur place',
];

// Catalogue des prestations vidéo (10 par défaut, comme la maquette)
const DEFAULT_PRESTATIONS = [
  { key: 'court',       designation: 'Vidéo courte pour réseaux sociaux (15s - 60s)', icon: 'video',      prix: 350000, qte: 1, checked: false },
  { key: 'pub',         designation: 'Vidéo publicitaire (30s - 90s)',                 icon: 'play',       prix: 600000, qte: 1, checked: false },
  { key: 'insti',       designation: 'Vidéo institutionnelle (3 - 5 min)',             icon: 'film',       prix: 850000, qte: 1, checked: true  },
  { key: 'doc',         designation: 'Vidéo documentaire (5 - 15 min)',                icon: 'camera',     prix: 1200000, qte: 1, checked: false },
  { key: 'event',       designation: 'Vidéo événementielle (reportage)',               icon: 'users',      prix: 700000, qte: 1, checked: false },
  { key: 'motion',      designation: 'Motion design / Animation',                       icon: 'sparkles',   prix: 450000, qte: 1, checked: false },
  { key: 'captation',   designation: 'Captation multi-caméras',                         icon: 'camera',     prix: 500000, qte: 1, checked: false },
  { key: 'montage',     designation: 'Montage & Habillage',                             icon: 'wand',       prix: 600000, qte: 1, checked: true  },
  { key: 'voixoff',     designation: 'Voix off',                                        icon: 'mic',        prix: 120000, qte: 1, checked: true  },
  { key: 'musique',     designation: 'Musique libre de droits',                         icon: 'music',      prix: 50000,  qte: 1, checked: true  },
];

const DEFAULT_OPTIONS = [
  { key: 'sous_titres',   designation: 'Sous-titres',                             prix: 80000,  qte: 1, checked: false },
  { key: 'etalonnage',    designation: 'Étalonnage / Colorimétrie',               prix: 100000, qte: 1, checked: false },
  { key: 'format_supp',   designation: 'Format supplémentaire (Vertical, Carré…)', prix: 70000,  qte: 1, checked: false },
];

const ICON_MAP = {
  'video': Video,
  'play': PlayCircle,
  'film': Film,
  'camera': Camera,
  'users': Users,
  'sparkles': Sparkles,
  'wand': Wand2,
  'mic': Mic,
  'music': Music,
};

export default function ProductionVideoDevisModal({ open, onOpenChange, onSaved }) {
  const [prospects, setProspects] = useState([]);
  const [loadingProspects, setLoadingProspects] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedProspect, setSelectedProspect] = useState(null);

  const [projet, setProjet] = useState({
    objectif: 'Vidéo institutionnelle',
    type: 'Vidéo longue',
    duree: '3 - 5 minutes',
    langue: 'Français',
    date_livraison: '',
  });

  const [prestations, setPrestations] = useState(DEFAULT_PRESTATIONS.map((p) => ({ ...p })));
  const [options, setOptions] = useState(DEFAULT_OPTIONS.map((o) => ({ ...o })));
  const [transport, setTransport] = useState({ mode: 'Transport routier', lieu: '', prix: 0 });
  const [notes, setNotes] = useState('');
  const [validiteDate, setValiditeDate] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelectedProspect(null);
    setPrestations(DEFAULT_PRESTATIONS.map((p) => ({ ...p })));
    setOptions(DEFAULT_OPTIONS.map((o) => ({ ...o })));
    setTransport({ mode: 'Transport routier', lieu: '', prix: 0 });
    setNotes('');
    const d = new Date();
    d.setDate(d.getDate() + 30);
    setValiditeDate(d.toISOString().slice(0, 10));
    const d2 = new Date();
    d2.setDate(d2.getDate() + 45);
    setProjet((p) => ({ ...p, date_livraison: d2.toISOString().slice(0, 10) }));

    const load = async () => {
      setLoadingProspects(true);
      try {
        const r = await axios.get('/prospects');
        setProspects(r.data || []);
      } catch (e) {
        toast.error('Impossible de charger les prospects');
      } finally {
        setLoadingProspects(false);
      }
    };
    load();
  }, [open]);

  const filteredProspects = useMemo(() => {
    if (!search) return prospects;
    const t = search.toLowerCase();
    return prospects.filter((p) =>
      (p.entreprise || '').toLowerCase().includes(t) ||
      (p.contact || '').toLowerCase().includes(t) ||
      (p.email || '').toLowerCase().includes(t) ||
      (p.telephone || '').toLowerCase().includes(t)
    );
  }, [prospects, search]);

  const updatePresta = (key, field, value) => {
    setPrestations((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));
  };
  const removePresta = (key) => setPrestations((prev) => prev.filter((it) => it.key !== key));
  const addPresta = () => {
    const name = window.prompt('Nom de la prestation à ajouter :');
    if (!name || !name.trim()) return;
    setPrestations((prev) => [
      ...prev,
      { key: `custom_${Date.now()}`, designation: name.trim(), icon: 'video', prix: 0, qte: 1, checked: true },
    ]);
  };

  const updateOption = (key, field, value) => {
    setOptions((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));
  };

  const checkedPrestations = prestations.filter((p) => p.checked && (Number(p.qte) || 0) > 0);
  const checkedOptions = options.filter((o) => o.checked && (Number(o.qte) || 0) > 0);
  const sousTotalProd = checkedPrestations.reduce((s, it) => s + (Number(it.prix) || 0) * (Number(it.qte) || 0), 0);
  const sousTotalOpt = checkedOptions.reduce((s, it) => s + (Number(it.prix) || 0) * (Number(it.qte) || 0), 0);
  const transportPrix = Number(transport.prix) || 0;
  const totalGeneral = sousTotalProd + sousTotalOpt + transportPrix;
  const [tva, setTva] = useTva();
  const tvaMt = tvaAmount(totalGeneral, tva);
  const canSave = selectedProspect && checkedPrestations.length > 0;

  const save = async () => {
    if (!selectedProspect) { toast.error('Sélectionnez d\'abord une entreprise'); return; }
    if (checkedPrestations.length === 0) { toast.error('Cochez au moins une prestation vidéo'); return; }
    setSaving(true);
    try {
      const items = [
        ...checkedPrestations.map((p) => ({
          designation: p.designation,
          quantite: parseInt(p.qte, 10) || 1,
          prix_unitaire: parseFloat(p.prix) || 0,
        })),
        ...checkedOptions.map((o) => ({
          designation: `[Option] ${o.designation}`,
          quantite: parseInt(o.qte, 10) || 1,
          prix_unitaire: parseFloat(o.prix) || 0,
        })),
      ];
      const notesFull = [
        `Objectif : ${projet.objectif}`,
        `Type : ${projet.type}`,
        `Durée : ${projet.duree}`,
        `Langue : ${projet.langue}`,
        `Livraison souhaitée : ${projet.date_livraison || '—'}`,
        notes ? `\n${notes}` : '',
      ].filter(Boolean).join('\n');
      const payload = {
        service_id: 'production-video',
        service_titre: 'Production vidéo',
        client_nom: selectedProspect.entreprise || '',
        client_email: selectedProspect.email || '',
        client_telephone: selectedProspect.telephone || '',
        client_adresse: selectedProspect.adresse || '',
        client_prospect_id: selectedProspect.id || null,
        supports_produits: items,
        transport_mode: transport.mode,
        transport_lieu: transport.lieu,
        transport_prix: transportPrix,
        notes: notesFull,
        validite_date: validiteDate,
        modele: 'Production vidéo',
        prix_unitaire: 0,
        quantite: 0,
        remise_pct: 0,
        tva_pct: tvaPct(tva),
        prestations: [],
      };
      const r = await axios.post('/conception/devis', payload);
      toast.success(`Devis vidéo ${r.data.numero} généré`);
      onSaved && onSaved(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error('Erreur lors de la génération du devis');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[1500px] w-[98vw] h-[93vh] p-0 overflow-hidden flex flex-col"
        data-testid="production-video-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white sticky top-0 z-10">
          <div>
            <div className="text-xs text-slate-500 font-medium">Production ▸ Devis vidéo</div>
            <h2 className="text-2xl font-bold text-slate-900">Nouveau devis vidéo</h2>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="btn-back-video-devis">
              <ArrowLeft className="h-4 w-4 mr-1" /> Retour
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Enregistrer brouillon</Button>
            <Button
              className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={save}
              disabled={!canSave || saving}
              data-testid="btn-generate-video-devis"
            >
              <FileText className="h-4 w-4 mr-1.5" />
              {saving ? 'Génération…' : 'Générer le devis'}
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-[1fr_400px]">
          <div className="overflow-y-auto p-5 space-y-5 bg-slate-50">
            {/* 1-2-3-4 */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* 1. Prospects */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <div className="text-sm font-bold text-slate-800">1. Choisir l&apos;entreprise</div>
                  <QuickAddEntrepriseButton onCreated={(p) => { setProspects(prev => [p, ...prev]); setSelectedProspect(p); }} />
                </div>
                <div className="relative mb-2">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <Input className="pl-8" placeholder="Rechercher une entreprise…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="video-input-search-prospect" />
                </div>
                <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
                  {loadingProspects && <div className="text-xs text-slate-500 p-2">Chargement…</div>}
                  {!loadingProspects && filteredProspects.length === 0 && (
                    <div className="text-xs text-slate-500 p-2 italic">Aucun prospect</div>
                  )}
                  {filteredProspects.map((p) => {
                    const sel = selectedProspect?.id === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setSelectedProspect(p)}
                        className={`w-full text-left rounded-lg p-2.5 border transition-colors ${sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-800 border-slate-200 hover:bg-slate-50'}`}
                        data-testid={`video-prospect-item-${p.id}`}
                      >
                        <div className="text-sm font-semibold truncate">{p.entreprise || 'Sans nom'}</div>
                        <div className={`text-xs truncate ${sel ? 'text-blue-100' : 'text-slate-500'}`}>{p.email || '—'}</div>
                        <div className={`text-xs ${sel ? 'text-blue-100' : 'text-slate-500'}`}>{p.telephone || '—'}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Infos */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-bold text-slate-800 mb-3">2. Informations de l&apos;entreprise</div>
                {selectedProspect ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Nom :</span><span className="font-semibold text-slate-800">{selectedProspect.entreprise || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Contact :</span><span className="text-slate-800">{selectedProspect.contact || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Email :</span><span className="text-blue-600 break-all">{selectedProspect.email || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Téléphone :</span><span className="text-slate-800">{selectedProspect.telephone || '—'}</span></div>
                    <div className="flex gap-2"><span className="text-slate-500 w-20 shrink-0">Adresse :</span><span className="text-slate-800">{selectedProspect.adresse || '—'}</span></div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-40 text-slate-400 text-sm italic">
                    <Building2 className="h-8 w-8 mb-2" />
                    Sélectionnez une entreprise
                  </div>
                )}
              </div>

              {/* 3. Détails projet vidéo */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Video className="h-4 w-4" />3. Détails du projet vidéo</div>
                <div className="space-y-2.5">
                  <div>
                    <Label className="text-xs">Objectif du projet</Label>
                    <Select value={projet.objectif} onValueChange={(v) => setProjet({ ...projet, objectif: v })}>
                      <SelectTrigger data-testid="video-objectif"><SelectValue /></SelectTrigger>
                      <SelectContent>{OBJECTIFS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Type de vidéo</Label>
                    <Select value={projet.type} onValueChange={(v) => setProjet({ ...projet, type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TYPES_VIDEO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Durée estimée</Label>
                    <Select value={projet.duree} onValueChange={(v) => setProjet({ ...projet, duree: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{DUREES.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Langue</Label>
                    <Select value={projet.langue} onValueChange={(v) => setProjet({ ...projet, langue: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{LANGUES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Date souhaitée de livraison</Label>
                    <Input type="date" value={projet.date_livraison} onChange={(e) => setProjet({ ...projet, date_livraison: e.target.value })} data-testid="video-date-livraison" />
                  </div>
                </div>
              </div>

              {/* 4. Transport */}
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-1.5"><Truck className="h-4 w-4" />4. Transport</div>
                <div className="space-y-2.5">
                  <div>
                    <Label className="text-xs">Mode de transport</Label>
                    <Select value={transport.mode} onValueChange={(v) => setTransport({ ...transport, mode: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{TRANSPORT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Lieu de livraison</Label>
                    <Input value={transport.lieu} onChange={(e) => setTransport({ ...transport, lieu: e.target.value })} placeholder="Ex. Douala" />
                  </div>
                  <div>
                    <Label className="text-xs">Prix du transport (XAF)</Label>
                    <Input type="number" min={0} value={transport.prix} onChange={(e) => setTransport({ ...transport, prix: e.target.value })} data-testid="video-transport-prix" />
                  </div>
                  <div className="rounded-md bg-blue-50 border border-blue-100 text-blue-700 text-xs px-2.5 py-2">
                    ⓘ Vous pouvez saisir le prix manuellement.
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Prestations vidéo */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm font-bold text-slate-800">5. Prestations vidéo</div>
                <Button onClick={addPresta} className="bg-blue-600 hover:bg-blue-700 text-white" size="sm" data-testid="btn-add-video-presta">
                  <Plus className="h-4 w-4 mr-1" /> Ajouter une prestation
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3">
                {prestations.map((it) => {
                  const Icon = ICON_MAP[it.icon] || Video;
                  return (
                    <div
                      key={it.key}
                      className={`border rounded-xl p-3 transition-all ${it.checked ? 'border-blue-500 bg-blue-50/40 ring-1 ring-blue-200' : 'border-slate-200 bg-white'}`}
                      data-testid={`video-presta-${it.key}`}
                    >
                      <label className="flex items-start gap-2 cursor-pointer mb-2">
                        <Checkbox
                          checked={it.checked}
                          onCheckedChange={(v) => updatePresta(it.key, 'checked', !!v)}
                          data-testid={`video-check-${it.key}`}
                          className="mt-0.5"
                        />
                        <div className="flex-1 flex items-center gap-2">
                          <Icon className="h-4 w-4 text-slate-500" />
                          <span className="text-sm font-medium text-slate-800 leading-tight">{it.designation}</span>
                        </div>
                      </label>
                      <div className="space-y-1.5">
                        <div>
                          <Label className="text-[10px] text-slate-500">Quantité</Label>
                          <Input type="number" min={0} value={it.qte} onChange={(e) => updatePresta(it.key, 'qte', e.target.value)} className="h-8 text-sm text-right" />
                        </div>
                        <div>
                          <Label className="text-[10px] text-slate-500">Prix unitaire (XAF)</Label>
                          <Input type="number" min={0} value={it.prix} onChange={(e) => updatePresta(it.key, 'prix', e.target.value)} className="h-8 text-sm text-right" data-testid={`video-prix-${it.key}`} />
                        </div>
                      </div>
                      <div className="flex justify-end mt-1.5">
                        <button className="text-red-500 hover:text-red-700" onClick={() => removePresta(it.key)} title="Retirer">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Options supplémentaires */}
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="text-sm font-bold text-slate-800 mb-3">Options supplémentaires</div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {options.map((op) => (
                  <div key={op.key} className={`border rounded-lg p-3 ${op.checked ? 'border-blue-400 bg-blue-50/40' : 'border-slate-200 bg-white'}`} data-testid={`video-option-${op.key}`}>
                    <label className="flex items-center gap-2 cursor-pointer mb-2">
                      <Checkbox
                        checked={op.checked}
                        onCheckedChange={(v) => updateOption(op.key, 'checked', !!v)}
                        data-testid={`video-check-option-${op.key}`}
                      />
                      <span className="text-sm font-medium text-slate-800">{op.designation}</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-slate-500">Quantité</Label>
                        <Input type="number" min={0} value={op.qte} onChange={(e) => updateOption(op.key, 'qte', e.target.value)} className="h-8 text-sm text-right" />
                      </div>
                      <div>
                        <Label className="text-[10px] text-slate-500">Prix (XAF)</Label>
                        <Input type="number" min={0} value={op.prix} onChange={(e) => updateOption(op.key, 'prix', e.target.value)} className="h-8 text-sm text-right" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Récapitulatif */}
          <div className="border-l border-slate-200 bg-white flex flex-col overflow-hidden">
            <div className="p-5 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-slate-900">Récapitulatif du devis</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="video-recap-panel">
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr className="text-slate-500 text-xs uppercase">
                      <th className="text-left px-3 py-2 font-semibold">Désignation</th>
                      <th className="text-right px-2 py-2 font-semibold w-14">Qté</th>
                      <th className="text-right px-2 py-2 font-semibold w-20">PU</th>
                      <th className="text-right px-3 py-2 font-semibold w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {checkedPrestations.length + checkedOptions.length === 0 && (
                      <tr><td colSpan={4} className="text-center py-6 text-slate-400 italic text-xs">Cochez au moins une prestation</td></tr>
                    )}
                    {checkedPrestations.map((it) => (
                      <tr key={it.key} className="border-t border-slate-100">
                        <td className="px-3 py-2 text-slate-800 leading-tight" title={it.designation}>{it.designation}</td>
                        <td className="px-2 py-2 text-right text-slate-800">{it.qte}</td>
                        <td className="px-2 py-2 text-right text-slate-800">{fmtXAF(it.prix)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">{fmtXAF(Number(it.prix) * Number(it.qte))}</td>
                      </tr>
                    ))}
                    {checkedOptions.map((it) => (
                      <tr key={it.key} className="border-t border-slate-100 bg-slate-50/30">
                        <td className="px-3 py-2 text-slate-700 text-xs italic">Option : {it.designation}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{it.qte}</td>
                        <td className="px-2 py-2 text-right text-slate-700">{fmtXAF(it.prix)}</td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-800">{fmtXAF(Number(it.prix) * Number(it.qte))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between border-t pt-3">
                  <span className="text-slate-600">Sous-total production</span>
                  <span className="font-semibold text-slate-900" data-testid="video-recap-sous-total-prod">{fmtXAF(sousTotalProd)}</span>
                </div>
                {sousTotalOpt > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-600">Sous-total options</span>
                    <span className="font-semibold text-slate-900">{fmtXAF(sousTotalOpt)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-600">Transport</span>
                  <span className="font-semibold text-slate-900">{fmtXAF(transportPrix)}</span>
                </div>
                <TvaToggle tva={tva} setTva={setTva} base={totalGeneral} testid="video-tva" />
                <div className="flex justify-between pt-3 border-t text-base">
                  <span className="font-bold text-slate-900">{tva.on ? 'Total TTC (XAF)' : 'Total général (XAF)'}</span>
                  <span className="font-bold text-blue-700 text-xl" data-testid="video-recap-total">{fmtXAF(totalGeneral + tvaMt)}</span>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea rows={3} placeholder="Ajoutez une note (optionnel)…" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div>
                <Label>Validité du devis</Label>
                <Input type="date" value={validiteDate} onChange={(e) => setValiditeDate(e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
